import React, { useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BellSimpleRinging, CheckCircle, XCircle } from "@phosphor-icons/react";
import Brand from "@/components/Brand";

// Public "Subscribe for plate alerts" opt-in.
// Uses the browser Web Push API to obtain a subscription tied to the site's
// VAPID public key, then POSTs {plate_number, endpoint, keys} to the public
// enrollment endpoint. On violation issue, the backend fires a targeted push
// to every subscription enrolled against that plate.
export default function PlateAlerts() {
    const { t } = useI18n();
    const [plate, setPlate] = useState("");
    const [busy, setBusy] = useState(false);
    const [enrolled, setEnrolled] = useState(false);

    const subscribe = async (e) => {
        e?.preventDefault?.();
        const pnum = plate.trim().toUpperCase();
        if (!pnum) { toast.error(t("plateNumber")); return; }
        setBusy(true);
        try {
            if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
                throw new Error(t("plateAlertsNotSupported"));
            }
            const reg = await navigator.serviceWorker.ready;
            const perm = await Notification.requestPermission();
            if (perm !== "granted") throw new Error(t("plateAlertsPermissionDenied"));
            const { data: vapid } = await api.get("/push/vapid-public");
            const pk = urlBase64ToUint8Array(vapid.public_key);
            const sub = await reg.pushManager.subscribe({
                userVisibleOnly: true, applicationServerKey: pk,
            });
            const raw = sub.toJSON();
            await api.post("/public/plate-alerts/enroll", {
                plate_number: pnum,
                endpoint: raw.endpoint,
                keys: raw.keys,
            });
            setEnrolled(true);
            toast.success(t("plateAlertsEnrolled"));
        } catch (err) {
            toast.error(err?.response?.data?.detail || err.message || t("error"));
        }
        setBusy(false);
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <header className="px-6 py-4 border-b border-border">
                <Brand />
            </header>
            <div className="flex-1 flex items-center justify-center p-6">
                <Card className="w-full max-w-md p-6 rounded-sm border-border space-y-4" data-testid="plate-alerts-page">
                    <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· PUBLIC</div>
                    <h1 className="text-2xl font-bold tracking-tight">{t("plateAlertsTitle")}</h1>
                    <p className="text-sm text-muted-foreground">{t("plateAlertsHint")}</p>
                    {enrolled ? (
                        <div className="flex items-center gap-2 text-green-700 text-sm" data-testid="plate-alerts-enrolled">
                            <CheckCircle size={18} weight="fill" />
                            {t("plateAlertsEnrolledLong")}
                        </div>
                    ) : (
                        <form onSubmit={subscribe} className="space-y-3">
                            <div>
                                <Label className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground">{t("plateNumber")}</Label>
                                <Input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} placeholder="DEMO-GRN-001" className="mono h-11 mt-1" data-testid="plate-alerts-input" required />
                            </div>
                            <Button type="submit" disabled={busy || !plate.trim()} className="w-full h-11 bg-primary hover:bg-primary/90 rounded-sm" data-testid="plate-alerts-subscribe">
                                <BellSimpleRinging size={16} weight="bold" className="me-1" />
                                {busy ? t("loading") : t("plateAlertsSubscribe")}
                            </Button>
                            <div className="text-[11px] text-muted-foreground">{t("plateAlertsPrivacy")}</div>
                        </form>
                    )}
                </Card>
            </div>
        </div>
    );
}

function urlBase64ToUint8Array(base64) {
    const padding = "=".repeat((4 - base64.length % 4) % 4);
    const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(b64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
}
